import { Injectable } from '@angular/core';

import { OrbitService } from '../../../services/orbit.service';
import { ObjectResizeControls } from '../../../custom/Controls';
import { TouchAndMouseMap } from '../../touch-and-mouse-map';
import { StageService } from '../../../services/stage.service';
import { SceneUtilsService } from '../../../services/scene-utils.service';
import { Select3dService } from '../../../services/select-3d.service';
import { Subscription } from 'rxjs/Subscription';
import { IValidator, CollisionValidator, WallRangeValidator } from './Validators';
import { WallCutService } from '../../../services/wall/wall-cut.service';
import { NewSizeCalculator, ISizeCalculator } from './NewSizeCalculator';
import { WorldSpaceOffsetCalculator } from './WorldSpaceOffsetCalculator';
import { ScaleInfo } from './ScaleInfo';
import { ObjectResizer } from './ObjectResizer';
import { Drag3D } from '../../3D/Drag/drag3-d.service';

declare const THREE: any;

@Injectable()
export class WindowDoorResizeComponent extends TouchAndMouseMap {
  private _controls: ObjectResizeControls;
  private _raycaster = new THREE.Raycaster();
  private _selectObjSubscription: Subscription;
  private _drag3dUnsubscribe;
  private _isResizing = false;
  private _objectToResize;
  private _resizingObjectWorldPosition = new THREE.Vector3();
  private _validators: IValidator[];
  private _offsetCalculator = new WorldSpaceOffsetCalculator();
  private _scaledSizeCalculator = NewSizeCalculator.Create();
  private _newSizeCalculator: ISizeCalculator;
  private _scaleInfo: ScaleInfo = new ScaleInfo();
  private _objectResizer = new ObjectResizer();
  private _objectToResizeEntity;

  constructor(
    private stage: StageService,
    private SceneUtilsSrvc: SceneUtilsService,
    private Select3D: Select3dService,
    private orbitControl: OrbitService,
    private wallCutSrvc: WallCutService,
    Drag3DComponent: Drag3D,
    collisionValidator: CollisionValidator,
    inWallRangeValidator: WallRangeValidator
  ) {
    super();
    this._controls = new ObjectResizeControls();
    this._validators = [collisionValidator, inWallRangeValidator];
    this._selectObjSubscription = this.Select3D.selectedWindowDoorSubject.subscribe(objects => {
      if (objects.length === 1) {
        this.stage.add(this._controls);
        this._objectToResize = objects[0];
        this._controls.update(this._objectToResize);
        this._controls.visible = true;
      } else {
        this._controls.visible = false;
        this._objectToResize = null;
      }
    });
    this._drag3dUnsubscribe = Drag3DComponent.on('drag', () => {
      this._controls.visible = false;
    });
  }

  hold(e: MouseEvent) {
    if (this._controls.visible) {
      this.SceneUtilsSrvc.getRay(e.offsetX, e.offsetY, this._raycaster);
      const intersection = this._raycaster.intersectObjects(this._controls.children);
      if (intersection.length !== 0) {
        this._isResizing = true;
        const button = intersection[0].object;
        const { _objectToResize: objectToResize, _scaleInfo: scaleInfo } = this;
        this._objectToResizeEntity = JSON.parse(JSON.stringify(objectToResize.userData.entity));
        const { length, height } = this._objectToResizeEntity;

        scaleInfo.calculate(objectToResize, button.name);

        this.orbitControl.disable();

        objectToResize.getWorldPosition(this._resizingObjectWorldPosition);

        const offset = this._offsetCalculator.calculate(scaleInfo, length / 2, height / 2).negate();

        this._newSizeCalculator = this._scaledSizeCalculator
          .setObjectWorldPosition(this._resizingObjectWorldPosition)
          .setOffset(offset)
          .setNormal(scaleInfo.planeNormal);

        return false;
      } else {
        this._controls.visible = false;
      }
    }
  }

  move(e: MouseEvent) {
    if (this._isResizing && this._controls.visible) {
      this.SceneUtilsSrvc.getRay(e.offsetX, e.offsetY, this._raycaster);
      const { _objectToResize: objectToResize, _scaleInfo: scaleInfo, _objectToResizeEntity: objectToResizeEntity } = this;
      const { length, height, width } = objectToResize.userData.entity;
      const newSize = this._newSizeCalculator.calculate(
        this._raycaster.ray,
        scaleInfo.worldScaleDirection
      );

      const currentSize = {
        length,
        height
      };

      const newObjectSize = {
        length: scaleInfo.localScaleDirection.x !== 0 ? newSize : currentSize.length,
        height: scaleInfo.localScaleDirection.y !== 0 ? newSize : currentSize.height,
        depth: width
      };

      const offset = this._offsetCalculator.calculate(
        scaleInfo,
        newObjectSize.length - currentSize.length,
        newObjectSize.height - currentSize.height
      );

      this._resizingObjectWorldPosition.add(offset);

      const isValid = this._validators.every(validator =>
        validator.validate(
          objectToResize,
          new THREE.Vector3().set(newObjectSize.length, newObjectSize.height, newObjectSize.depth),
          this._resizingObjectWorldPosition
        )
      );

      if (isValid) {
        this._objectResizer.resize({
          object: objectToResize,
          currentSize,
          newSize: newObjectSize,
          scaleInfo,
          includeChildren: true
        });

        objectToResizeEntity.length = newObjectSize.length;
        objectToResizeEntity.height = newObjectSize.height;
        objectToResize.userData.entity = objectToResizeEntity;

        this.wallCutSrvc.cutHoles(objectToResize.parent);
        objectToResize.getWorldPosition(this._resizingObjectWorldPosition);
        this._controls.update(objectToResize);
      }

      return false;
    }
  }

  release(e) {
    if (this._isResizing) {
      this.orbitControl.enable();
    }
    this._isResizing = false;
  }

  dispose() {
    super.dispose();
    this._selectObjSubscription.unsubscribe();
    this._drag3dUnsubscribe();
  }

  disable() {
    super.disable();
    this._controls.visible = false;
  }
}



// WEBPACK FOOTER //
// ./src/app/Component(2D and 3D)/Shared/ObjectResize/WindowDoorResizeComponent.ts